import { Nullable } from '../common';
import { MemberState } from '../../interfaces/user';
import { MenuItemIndividualItem } from '../../atomic/molecules/Dropdown_V2/interfaces';
import { AutocompleteDropdownItem } from '../../atomic/organism/Autocomplete/interfaces';

import {
  PARTICIPANTS_AND_VIEWERS_LABEL,
  PARTICIPANTS_LABEL,
  VIEWERS_LABEL,
} from '../../languages/en/flows/sharesheet';

import {
  AccessRole,
  CriteriaResponse,
  GetFlowDetailsResponse,
  Rule,
} from '../../queries/Flows/interfaces';

import {
  SimplifiedShareSheetCardData,
  SimplifiedShareSheetDataTypes,
  VisibilityTypes,
} from '../../controllers/flows/FlowsShareSheetController/types';

const getOthersCriteriaForParticipation = (
  flowBuilderData: Nullable<GetFlowDetailsResponse>,
) => {
  return flowBuilderData?.data.integrations?.participationNotification &&
    flowBuilderData.data.integrations.participationNotification.length > 0 &&
    flowBuilderData.data.integrations.participationNotification[0].metaData
    ? [
        {
          field: 'integration',
          operator: 'is',
          value: {
            from: 'slack',
            model: [
              {
                connectionId:
                  flowBuilderData.data.integrations.participationNotification[0]
                    .connectionId,
                source: 'channel',
                choose:
                  flowBuilderData.data.integrations.participationNotification[0].metaData.channelInfo.map(
                    (channel) => ({
                      id: channel.id,
                      name: channel.name,
                    }),
                  ),
              },
            ],
          },
        },
      ]
    : undefined;
};

const getOthersCriteriaForVisibility = (
  flowBuilderData: Nullable<GetFlowDetailsResponse>,
) => {
  return flowBuilderData &&
    flowBuilderData.data.integrations &&
    flowBuilderData.data.integrations.responseNotification &&
    flowBuilderData.data.integrations.responseNotification.length > 0 &&
    flowBuilderData.data.integrations.responseNotification[0].metaData
    ? [
        {
          field: 'integration',
          operator: 'is',
          value: {
            from: 'slack',
            model: [
              {
                connectionId:
                  flowBuilderData.data.integrations.responseNotification[0]
                    .connectionId,
                source: 'channel',
                choose:
                  flowBuilderData.data.integrations.responseNotification[0].metaData.channelInfo.map(
                    (channel) => ({
                      id: channel.id,
                      name: channel.name,
                    }),
                  ),
              },
            ],
          },
        },
      ]
    : undefined;
};

export const getCriteriaPayloadForParticipation = (
  criteria: Nullable<any>,
  flowBuilderData: Nullable<GetFlowDetailsResponse>,
) => {
  const others = getOthersCriteriaForParticipation(flowBuilderData);

  if (criteria) {
    return {
      criteria: {
        ...criteria,
        others: others,
      },
    };
  } else {
    return {
      criteria: {
        custom: {
          condition: 'or',
          rules: [],
        },
        others: others,
      },
    };
  }
};

export const getCriteriaPayloadForVisibility = (
  criteria: Nullable<any>,
  flowBuilderData: Nullable<GetFlowDetailsResponse>,
  visibilityType: VisibilityTypes,
) => {
  let others = getOthersCriteriaForVisibility(flowBuilderData);

  if (visibilityType === VisibilityTypes.PARTICIPANTS_ONLY) {
    if (
      flowBuilderData &&
      flowBuilderData.data.integrations &&
      flowBuilderData.data.integrations.participationNotification &&
      flowBuilderData.data.integrations.participationNotification.length > 0 &&
      flowBuilderData.data.integrations.participationNotification[0].metaData
    ) {
      if (others) {
        others[0].value.model[0].choose = [
          ...others[0].value.model[0].choose,
          ...flowBuilderData.data.integrations.participationNotification[0].metaData.channelInfo.map(
            (channel) => ({
              id: channel.id,
              name: channel.name,
            }),
          ),
        ];
      } else {
        others = getOthersCriteriaForParticipation(flowBuilderData);
      }
    }
  }

  if (visibilityType === VisibilityTypes.OWNER_ONLY) {
    return {
      criteria: {
        custom: {
          condition: 'or',
          rules: [
            {
              field: 'member',
              operator: 'is',
              value: [flowBuilderData?.data.owner.memberID],
            },
          ],
        },
        others: others,
      },
    };
  }

  if (criteria) {
    return {
      criteria: {
        ...criteria,
        others: others,
      },
    };
  } else {
    return {
      criteria: {
        custom: {
          condition: 'or',
          rules: [],
        },
        others: others,
      },
    };
  }
};

export const getLinkRole = (linkAccessRoles: AccessRole[]) => {
  if (linkAccessRoles.includes(AccessRole.VIEWER)) {
    return VIEWERS_LABEL;
  }
  if (
    linkAccessRoles.includes(AccessRole.VIEWER) &&
    linkAccessRoles.includes(AccessRole.PARTICIPANT)
  ) {
    return PARTICIPANTS_AND_VIEWERS_LABEL;
  }
  return PARTICIPANTS_LABEL;
};

export const getUpdatedSelectedItemsBasedOnDropdownSelection = ({
  currentUserId,
  setting,
  selectedItems,
}: {
  currentUserId: string;
  setting: MenuItemIndividualItem;
  selectedItems: SimplifiedShareSheetCardData[];
}) => {
  let modifiedSelectedItems = [...selectedItems];
  if (setting?.id === 'remove') {
    modifiedSelectedItems = [...selectedItems].filter(
      (i) => i.id !== currentUserId,
    );
  } else {
    const currentUserIndex = selectedItems.findIndex(
      (i) => i.id === currentUserId,
    );
    if (currentUserIndex > -1 && setting) {
      modifiedSelectedItems.splice(currentUserIndex, 1, {
        ...selectedItems[currentUserIndex],
        setting,
        icon:
          selectedItems[currentUserIndex]?.memberState ===
            MemberState.PENDING ||
          selectedItems[currentUserIndex]?.emailToInvite
            ? 'circular-pending-user'
            : selectedItems[currentUserIndex]?.icon,
      });
    }
  }
  return modifiedSelectedItems;
};

export const getShareSheetCardDataFromAutoCompleteOption = ({
  option,
  setting,
  type,
}: {
  option: AutocompleteDropdownItem<string>;
  type: SimplifiedShareSheetDataTypes;
  setting?: MenuItemIndividualItem;
}): SimplifiedShareSheetCardData =>
  option.emailToInvite
    ? {
        ...option,
        id: option.emailToInvite,
        setting,
        type,
        memberState: MemberState.PENDING,
        icon: 'circular-pending-user',
      }
    : {
        ...option,
        setting,
        type,
      };

export const serializeSimpleShareSheetRulesForAPIPayload = (
  selectedPeople: SimplifiedShareSheetCardData[],
): CriteriaResponse => {
  // We only support rule for members only
  // TODO: Sumedha: Fix type for Rule[] to auto detect the type properly
  if (selectedPeople.some((item) => item.id === 'everyone'))
    return { criteria: { everyone: true } } as CriteriaResponse;
  const criteriaRules = selectedPeople.map(
    (p) =>
      ({
        value: [p?.emailToInvite ? p?.emailToInvite : p.id],
        field: p?.emailToInvite ? 'email' : p.type,
        operator: p?.condition || 'is',
      } as Rule),
  );
  return {
    criteria: {
      custom: {
        rules: [
          {
            rules: criteriaRules,
            condition: 'or',
          },
        ],
        condition: 'or',
      },
    },
    isNewMembersAdded: false,
  };
};
